import { Injectable, inject } from '@angular/core';
import { GqlService, socialMutations } from '@tgx/shared/data-access/graphql';
import { ContactTypeString, PartnerType } from '@tgx/shared/enums';
import { BehaviorSubject, combineLatest, map, Observable } from 'rxjs';
import { getContactOrgPartnerType } from '../operations/queries/get-billing-contacts';
import { BillingService, ProfilesService } from '@tgx/shared/services';
import { BillingAccountInfo, BillingContractInfo } from '../interfaces/billing-contact-types';
import { SharedLoggingService } from '@tgx/shared/logging';
import { ContactInfo, ContactModification, ContactTypeData, ContactTypePayloadData } from '@tgx/shared/interfaces';
import { IBillingAccountContacts } from '../interfaces/contact-type';
import { ActionType, ContactTypeResponse } from '../enums/company-contacts.enum';

@Injectable({
  providedIn: 'root',
})
export class CompanyContactsService {
  private gqlService = inject(GqlService);
  private loggingService = inject(SharedLoggingService);
  private billingApiService = inject(BillingService);
  private profilesService = inject(ProfilesService);

  // received formated data from the billing query
  billingAccounts = {
    buyer: [] as BillingAccountInfo[],
    seller: [] as BillingAccountInfo[],
  };

  showBuyerBillingInput = new BehaviorSubject<boolean>(null);
  showSellerBillingInput = new BehaviorSubject<boolean>(null);
  hasSameBillingAccount = new BehaviorSubject<boolean>(null);

  readonly showBillingContactFilter$: Observable<boolean> = combineLatest([
    this.showBuyerBillingInput,
    this.showSellerBillingInput,
  ]).pipe(map(([buyer, seller]) => !!buyer || !!seller));

  // saved data from the form of billing
  billingContactType = {
    buyerKeyContacts: {
      add: [],
      delete: [],
    },
    sellerKeyContacts: {
      add: [],
      delete: [],
    },
  };

  // receive el billingAccount and constacts email
  private async getContactsOrgPartnerType(org: string): Promise<BillingContractInfo[]> {
    const body = {
      'where': {
        'Partner': {
          'is': {
            'orgCode': {
              'equals': org.toUpperCase(),
            },
          },
        },
        'partnerType': {
          'in': ['BUYER', 'SELLER'],
        },
        'endDate': null,
        'OR': [
          {
            'type': { 'notIn': [3] },
          },
          {
            'type': null,
          },
        ],
      },
    };
    return await new Promise((resolve, reject) => {
      this.gqlService
        .queryBilling(getContactOrgPartnerType, body)
        .then((response) => {
          const resp = response['contracts'];
          resolve(resp);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  // create or delete emails at billingContactType
  private async updateBillingContacts(): Promise<any> {
    const allPromises: Promise<any>[] = [];
    const billingMap = new Map<number, IBillingAccountContacts>();

    // generate Map with the list of emails non duplicated that will add or delete
    const populateBillingMap = (
      contacts: { email: string }[],
      action: (typeof ActionType)[keyof typeof ActionType],
      type: 'buyer' | 'seller',
    ) => {
      //iterate BillingAccounts with the query data
      for (const billingInfo of this.billingAccounts[type]) {
        const BillingAccountId: number = billingInfo.billingAccount;
        // generate key of billingAccount id
        if (!billingMap.has(BillingAccountId)) {
          billingMap.set(BillingAccountId, {
            add: new Set<string>(),
            delete: new Set<string>(),
          });
        }
        // add email to billingAccount id key to add or delete action
        const entry = billingMap.get(BillingAccountId);
        for (const contact of contacts) {
          if (action === ActionType.ADD) {
            entry.add.add(contact.email);
          } else if (action === ActionType.DELETE) {
            entry.delete.add(contact.email);
          }
        }
      }
    };
    // consult the buyerKeyContacts (ADD/DELETE) blocks as a buyer
    if (this.billingContactType.buyerKeyContacts.add.length > 0) {
      populateBillingMap(this.billingContactType.buyerKeyContacts.add, ActionType.ADD, PartnerType.BUYER);
    }
    if (this.billingContactType.buyerKeyContacts.delete.length > 0) {
      populateBillingMap(this.billingContactType.buyerKeyContacts.delete, ActionType.DELETE, PartnerType.BUYER);
    }

    // consult the sellerKeyContacts (ADD/DELETE) blocks as seller
    if (this.billingContactType.sellerKeyContacts.add.length > 0) {
      populateBillingMap(this.billingContactType.sellerKeyContacts.add, ActionType.ADD, PartnerType.SELLER);
    }
    if (this.billingContactType.sellerKeyContacts.delete.length > 0) {
      populateBillingMap(this.billingContactType.sellerKeyContacts.delete, ActionType.DELETE, PartnerType.SELLER);
    }

    // Iterate the Map and process every billingAccount id individual
    for (const [baId, { add, delete: remove }] of billingMap.entries()) {
      //  PROCESS ADD
      for (const email of add) {
        const billingContact = {
          email,
          receiveInvoices: true,
          BillingAccount: { connect: { id: baId } },
        };
        allPromises.push(this.billingApiService.createBillingAccountContact(billingContact));
      }
      // PROCESS DELETE
      // combines the query contacts of each billing partnerType
      const combinedBillings = [
        ...this.billingAccounts.buyer.filter((b) => b.billingAccount === baId),
        ...this.billingAccounts.seller.filter((b) => b.billingAccount === baId),
      ];
      // put together contacts only
      const allContacts = combinedBillings.reduce((acc, billingInfo) => {
        return [...acc, ...billingInfo.contacts];
      }, []);

      // remove duplicate contacts
      const uniqueContacts = allContacts.filter(
        (contact, index, array) => index === array.findIndex((c) => c.id === contact.id),
      );
      // iterate contacts and see if the emails match those that are in remove
      for (const contact of uniqueContacts) {
        if (remove.has(contact.email)) {
          allPromises.push(this.billingApiService.deleteBillingAccountContact({ id: contact.id }));
        }
      }
    }

    return Promise.all(allPromises);
  }

  public async getKeyContacts(orgCode: string, isBuyer: boolean, isSeller: boolean): Promise<ContactInfo> {
    const billingKeyContacts = await this.getBillingKeyContacts(orgCode, isBuyer, isSeller);
    const allCompanyContacts = await this.profilesService.getCompanyContacts(orgCode);

    return {
      ...allCompanyContacts,
      buyerKeyContacts: [...(allCompanyContacts?.buyerKeyContacts ?? []), ...billingKeyContacts.buyerKeyContacts],
      sellerKeyContacts: [...(allCompanyContacts?.sellerKeyContacts ?? []), ...billingKeyContacts.sellerKeyContacts],
    };
  }

  // Loads only the Billing contacts, so they can be shown and edited without waiting for the social key contacts.
  public async getBillingKeyContacts(orgCode: string, isBuyer: boolean, isSeller: boolean): Promise<ContactInfo> {
    this.billingContactType = {
      buyerKeyContacts: {
        add: [],
        delete: [],
      },
      sellerKeyContacts: {
        add: [],
        delete: [],
      },
    };

    this.billingAccounts = {
      buyer: [],
      seller: [],
    };
    this.hasSameBillingAccount.next(false);
    this.showBuyerBillingInput.next(false);
    this.showSellerBillingInput.next(false);
    const allBillingContactsPartnerType: BillingContractInfo[] = await this.getContactsOrgPartnerType(orgCode);

    if (
      allBillingContactsPartnerType.length >= 2 &&
      allBillingContactsPartnerType[0]?.BillingAccount?.id === allBillingContactsPartnerType[1]?.BillingAccount?.id
    ) {
      this.hasSameBillingAccount.next(true);
    }

    if (!allBillingContactsPartnerType?.length) {
      return { buyerKeyContacts: [], sellerKeyContacts: [] };
    }

    return this.fillBillingCompanyContacts(allBillingContactsPartnerType, isBuyer, isSeller);
  }

  // update social contactType
  private async updateCompanyContacts(orgCode: string, data: any): Promise<ContactInfo> {
    if (Object.keys(data).length === 0) {
      return Promise.resolve(null);
    }

    const body = {
      ownerCode: orgCode,
      data: data,
    };

    return await new Promise((resolve, reject) => {
      this.gqlService
        .mutationGateway(socialMutations.updateCompanyKeyContacts, body)
        .then((response) => {
          const contactData = response['social']['updateProfile']['profileData'];
          if (contactData) {
            resolve(contactData);
          } else if (response['social']['updateProfile']['adviseMessage']) {
            reject(response['social']['updateProfile']['adviseMessage']);
          } else {
            reject(null);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  public async updateKeyContacts(
    orgCode: string,
    addBuyer: ContactTypeData[],
    removeBuyer: ContactTypeData[],
    addSeller: ContactTypeData[],
    removeSeller: ContactTypeData[],
  ): Promise<any> {
    const buyerKeyContactsInput: ContactModification = {};
    const sellerKeyContactsInput: ContactModification = {};

    // clear previous billing data
    this.billingContactType.buyerKeyContacts.add = [];
    this.billingContactType.buyerKeyContacts.delete = [];
    this.billingContactType.sellerKeyContacts.add = [];
    this.billingContactType.sellerKeyContacts.delete = [];

    // only add property that is not void
    if (addBuyer.length > 0) {
      const filterContactType = this.parseBillingData(addBuyer, ContactTypeResponse.BUYER_KEY_CONTACTS, ActionType.ADD);
      buyerKeyContactsInput.add = filterContactType;
    }
    if (removeBuyer.length > 0) {
      const filterContactType = this.parseBillingData(
        removeBuyer,
        ContactTypeResponse.BUYER_KEY_CONTACTS,
        ActionType.DELETE,
      );
      buyerKeyContactsInput.delete = filterContactType;
    }

    if (addSeller.length > 0) {
      const filterContactType = this.parseBillingData(
        addSeller,
        ContactTypeResponse.SELLER_KEY_CONTACTS,
        ActionType.ADD,
      );
      sellerKeyContactsInput.add = filterContactType;
    }
    if (removeSeller.length > 0) {
      const filterContactType = this.parseBillingData(
        removeSeller,
        ContactTypeResponse.SELLER_KEY_CONTACTS,
        ActionType.DELETE,
      );
      sellerKeyContactsInput.delete = filterContactType;
    }

    const data: ContactTypePayloadData = {};

    if (Object.keys(buyerKeyContactsInput).some((key) => buyerKeyContactsInput[key].length > 0)) {
      data.buyerKeyContactsInput = buyerKeyContactsInput;
    }

    if (Object.keys(sellerKeyContactsInput).some((key) => sellerKeyContactsInput[key].length > 0)) {
      data.sellerKeyContactsInput = sellerKeyContactsInput;
    }

    return await new Promise((resolve, reject) => {
      const updateCompanyContacts = this.updateCompanyContacts(orgCode, data);
      const updateBillingContacts = this.updateBillingContacts();
      Promise.all([updateCompanyContacts, updateBillingContacts])
        .then((results) => {
          const errorMessage = {
            errors: [],
          };

          results.forEach((result) => {
            if (result?.status === 'rejected') {
              if (result.reason) {
                errorMessage.errors.push({ description: result.reason });
              }
            }
          });
          resolve(results);
          if (errorMessage.errors.length > 0) {
            resolve({ errorMessage });
          } else {
            resolve({ message: 'All operations were successful' });
          }
        })
        .catch((error) => {
          this.loggingService.logError(
            'Error updating contacts information',
            {
              file: 'company-contacts.service.ts',
              method: 'updateKeyContacts',
              description: error.message,
              stackTrace: error.stack,
            },
            true,
          );

          reject(error);
        });
    });
  }

  // obtain contactsType 'billing'
  private parseBillingData(
    contactTypes: ContactTypeData[],
    partnerKeyType: string,
    keyType: keyof ContactModification,
  ): ContactTypeData[] {
    // filter the contactType not 'billing' type
    const nonBillingContacts = contactTypes.filter((contact) => contact.contactType !== ContactTypeString.BILLING);

    // obtain contactType  of 'billing' type
    const billingContacts = contactTypes.filter((contact) => contact.contactType === ContactTypeString.BILLING);
    if (billingContacts.length > 0) {
      this.billingContactType[partnerKeyType][keyType] = billingContacts;
    }

    return nonBillingContacts;
  }

  private fillBillingCompanyContacts(
    billingContracts: BillingContractInfo[],
    isBuyer: boolean,
    isSeller: boolean,
  ): { buyerKeyContacts: ContactTypeData[]; sellerKeyContacts: ContactTypeData[] } {
    const buyerKeyContacts: ContactTypeData[] = [];
    const sellerKeyContacts: ContactTypeData[] = [];
    const BillingHasPartner = ['SELLER', 'BUYER'];

    //billing accounts
    for (const billingContract of billingContracts) {
      if (billingContract && billingContract.BillingAccount) {
        // enable the billing by partner type
        if (billingContract?.partnerType && BillingHasPartner.includes(billingContract?.partnerType)) {
          this.updateBillingAccountPresence(billingContract?.partnerType);
        }
        const baseBillingInfo: BillingAccountInfo = {
          billingAccount: billingContract.BillingAccount.id,
          contactType: ContactTypeString.BILLING,
          //role: billingContract.partnerType,
          partnerType: billingContract.partnerType,
          contacts: [],
        };

        // list of contacts at billing
        if (billingContract?.BillingAccount?.Contacts?.length > 0) {
          for (const contact of billingContract.BillingAccount.Contacts) {
            const contactInfo = {
              email: contact.email,
              id: contact.id,
              receiveInvoices: contact.receiveInvoices,
            };
            baseBillingInfo.contacts.push(contactInfo);

            const newContactType = {
              contactType: ContactTypeString.BILLING,
              email: contact.email,
            };

            if (isBuyer && baseBillingInfo?.partnerType === PartnerType.BUYER.toUpperCase()) {
              buyerKeyContacts.push(newContactType);
            }
            if (isSeller && baseBillingInfo?.partnerType === PartnerType.SELLER.toUpperCase()) {
              sellerKeyContacts.push(newContactType);
            }
          }
        }
        const target =
          isBuyer && baseBillingInfo?.partnerType === PartnerType.BUYER.toUpperCase()
            ? this.billingAccounts.buyer
            : isSeller && baseBillingInfo?.partnerType === PartnerType.SELLER.toUpperCase()
              ? this.billingAccounts.seller
              : null;
        if (target) {
          target.push(baseBillingInfo);
        }
      }
    }
    return { buyerKeyContacts, sellerKeyContacts };
  }

  // mark at buyer or seller if billing has billingAccount
  public updateBillingAccountPresence(partnerType: string) {
    if (partnerType === 'BUYER') {
      this.showBuyerBillingInput.next(true);
    }
    if (partnerType === 'SELLER') {
      this.showSellerBillingInput.next(true);
    }
  }
}
